import { useEffect, useState } from "react";
import { Check, Copy, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { SearchSelect } from "../../components/SearchSelect";
import { SettingsLayout } from "../../components/settings/SettingsLayout";
import { SyncCode } from "../../components/settings/SyncCode";
import { apiGet } from "../../components/crm/contactsKit";
import { InlineLoader } from "../../components/PageLoader";
import { NO_DATA } from "../../lib/format";

/**
 * Settings → Sync API docs (design: screens/settings/SyncApiDocs.tsx).
 *
 * Same layout and sections as the design, with the content corrected to the
 * real Sync API (backend routes/syncApiRoutes.js, mounted at /api/v1/sync, and
 * the old pages/settings/Integrations/SyncApiDocs.js): X-API-Key auth, PUT
 * upserts by your external ID, /care-plans for treatment plans, batch routes of
 * up to 100 items, cursor paging, 120 requests a minute, real error codes and
 * webhook events. Leads (item 292): GET /sync/leads, GET · PUT
 * /sync/leads/{external_id} (controllers/sync/leadController.js), scopes
 * leads:read and leads:write.
 * The clinic name in the header comes from GET /api/organizations/my-organization.
 */

const sections = [
  { id: "overview", label: "Overview", group: "Get started" },
  { id: "authentication", label: "Authentication", group: "Get started" },
  { id: "patients", label: "Patients", group: "Resources" },
  { id: "appointments", label: "Appointments", group: "Resources" },
  { id: "leads", label: "Leads", group: "Resources" },
  { id: "treatment-plans", label: "Treatment plans", group: "Resources" },
  { id: "bulk", label: "Bulk upsert", group: "Sync patterns" },
  { id: "pagination", label: "Cursor pagination", group: "Sync patterns" },
  { id: "conflicts", label: "Conflict rules", group: "Sync patterns" },
  { id: "webhooks", label: "Webhooks", group: "Events" },
  { id: "errors", label: "Errors and limits", group: "Reference" },
];
const base = "https://api.getolliai.com/api/v1";
const KEY = "sk_sync_…";

// Example body, as in the old docs page (PUT /patients/{external_id}).
const payload = { first_name: "Ramesh", last_name: "Kumar", phone: "9876500011", gender: "male", date_of_birth: "1985-04-12" };
const shaped = { external_id: "MRN-88213", id: 986, patient_number: "PAT-2026-0002", name: "Ramesh Kumar", first_name: "Ramesh", last_name: "Kumar", phone: "919876500011", date_of_birth: "1985-04-12", gender: "male" };

/** Per resource: the real routes, and three of its fields. */
const RESOURCES = {
  patients: { routes: "GET /sync/patients · GET · PUT /sync/patients/{external_id}", fields: [["external_id", "string", "Your PMS record identifier, in the URL"], ["phone", "string", "Any format; ten digits implies +91. Matches patients Olli already has"], ["first_name", "string", "Required"]] },
  appointments: { routes: "GET · POST /sync/appointments · PATCH /sync/appointments/{external_id}", fields: [["external_id", "string", "Your PMS record identifier"], ["date / start", "date / time", "YYYY-MM-DD and HH:MM, clinic time"], ["facility_external_id", "string", "Clinic location identifier; required with more than one branch"]] },
  // Item 292. A new lead goes through the same path as the CRM form: de-duplicated,
  // linked to a contact, scored and auto-assigned. An open lead with the same
  // number or email that no other external id has claimed is adopted.
  leads: { routes: "GET /sync/leads · GET · PUT /sync/leads/{external_id}", fields: [["external_id", "string", "Your system's enquiry identifier, in the URL"], ["first_name", "string", "Required"], ["last_name", "string", "Optional"], ["phone", "string", "Phone or email is required. Ten digits implies +91"], ["email", "string", "Phone or email is required"], ["source", "string", "manual, meta_facebook, meta_instagram, google_business, whatsapp, website_form, referral, walk_in, phone_call or other; anything else is saved as other"], ["status", "string", "One of the clinic's lead status keys; an unknown key is a 400"], ["notes", "string", "Optional, on a new lead"], ["GET filters", "query", "updated_since, status, phone, linked=true (only leads you've synced), cursor, limit (up to 100)"], ["Response", "object", "external_id, id, first_name, last_name, phone, email, source, status, assigned_to, created_at, updated_at. 201 when created, 200 when updated"], ["Scopes", "key", "leads:read for GET, leads:write for PUT"]] },
  "treatment-plans": { routes: "GET /sync/care-plans · PUT /sync/care-plans/{external_id}", fields: [["external_id", "string", "Your PMS record identifier, in the URL"], ["start_date", "date", "YYYY-MM-DD"], ["patient_external_id", "string", "Your ID for the patient"]] },
};

const ERRORS = [
  ["401", "UNAUTHORIZED", "No X-API-Key header."],
  ["401", "INVALID_API_KEY", "Unknown or deactivated key."],
  ["403", "PLAN_UPGRADE_REQUIRED", "Account is not on Growth or above."],
  ["403", "INSUFFICIENT_SCOPE", "The key lacks a scope; the response names it."],
  ["400", "VALIDATION_ERROR", "A field is missing or malformed."],
  ["404", "NOT_FOUND", "No such record."],
  ["404", "PRACTITIONER_NOT_FOUND", "A referenced record is not synced yet."],
  ["409", "SLOT_TAKEN", "The slot is booked. Alternatives are included."],
  ["409", "ALREADY_LINKED", "That record maps to a different id of yours."],
  ["422", "UNMAPPED_STATUS", "Teach the mapping first."],
  ["422", "IDEMPOTENCY_KEY_REUSED", "Same key, different body."],
  ["429", "RATE_LIMITED", "Wait for Retry-After before retrying."],
  ["400", "FACILITY_REQUIRED", "Multi-branch org: name the branch."],
  ["400", "BATCH_TOO_LARGE", "More than 100 items in one batch."],
  ["403", "NOT_A_TEST_KEY", "Only a test key may purge /sandbox."],
];
const EVENTS = ["appointment.created", "appointment.rescheduled", "appointment.cancelled", "appointment.status_changed", "patient.created", "patient.updated", "care_plan.status_changed"];

function requestCode(language, method, path, body) {
  const url = `${base}${path}`;
  const write = method !== "GET";
  if (language === "curl") {
    return `curl -X ${method} '${url}' \\\n  -H 'X-API-Key: ${KEY}'${write ? ` \\\n  -H 'Content-Type: application/json' \\\n  -H 'Idempotency-Key: pms-sync-88213'` : ""}${body ? ` \\\n  -d '${JSON.stringify(body)}'` : ""}`;
  }
  if (language === "node") {
    return `const response = await fetch("${url}", {\n  method: "${method}",\n  headers: {\n    "X-API-Key": "${KEY}"${write ? `,\n    "Content-Type": "application/json",\n    "Idempotency-Key": "pms-sync-88213"` : ""}\n  }${body ? `,\n  body: JSON.stringify(${JSON.stringify(body)})` : ""}\n});\nconst result = await response.json();`;
  }
  return `import requests\n\nresponse = requests.${method.toLowerCase()}(\n    "${url}",\n    headers={"X-API-Key": "${KEY}"${write ? `, "Idempotency-Key": "pms-sync-88213"` : ""}}${body ? `,\n    json=${JSON.stringify(body)}` : ""}\n)\nprint(response.json())`;
}

function ExampleCard({ title, method, path, body }) {
  const [language, setLanguage] = useState("curl");
  const [response, setResponse] = useState(false);
  const [copied, setCopied] = useState(false);
  const code = requestCode(language, method, path, body);
  const responseCode = JSON.stringify(method === "GET" ? { success: true, data: [shaped], next_cursor: null } : { success: true, data: shaped }, null, 2);
  return (
    <section className="overflow-hidden rounded-md bg-ink text-paper shadow-soft">
      <div className="flex flex-wrap items-center gap-2 border-b border-paper/10 px-4 py-3">
        <span className="rounded-xs bg-paper/10 px-2 py-1 font-mono text-caption font-semibold">{method}</span>
        <code className="min-w-0 flex-1 truncate font-mono text-caption text-paper/70">{path}</code>
        <button type="button" onClick={() => { void navigator.clipboard?.writeText(code); setCopied(true); window.setTimeout(() => setCopied(false), 1400); }} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-caption ring-1 ring-paper/20 hover:bg-paper/10">
          {copied ? <Check size={13} /> : <Copy size={13} />}{" "}{copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div role="tablist" aria-label={`${title} code language`} className="flex gap-4 border-b border-paper/10 px-4">
        {[{ id: "curl", label: "cURL" }, { id: "node", label: "Node.js" }, { id: "python", label: "Python" }].map((item) => (
          <button type="button" key={item.id} role="tab" aria-selected={language === item.id} onClick={() => setLanguage(item.id)} className={`border-b-2 py-2 text-caption ${language === item.id ? "border-sky text-paper" : "border-transparent text-paper/50 hover:text-paper"}`}>{item.label}</button>
        ))}
      </div>
      <SyncCode code={code} />
      <div className="border-t border-paper/10">
        <button type="button" onClick={() => setResponse(!response)} className="flex w-full items-center justify-between px-4 py-3 text-caption font-semibold">
          <span>Response example</span>
          <span className="font-mono text-success">200 OK</span>
        </button>
        {response && <SyncCode code={responseCode} />}
      </div>
    </section>
  );
}

export default function SyncApiDocs() {
  const [active, setActive] = useState("overview");
  const [query, setQuery] = useState("");
  const [orgName, setOrgName] = useState(null); // null while the clinic name loads
  const visible = sections.filter((section) => `${section.label} ${section.group}`.toLowerCase().includes(query.toLowerCase()));
  const groups = [...new Set(visible.map((section) => section.group))];

  useEffect(() => {
    let live = true;
    apiGet("/api/organizations/my-organization").then((r) => {
      const d = r?.data;
      const row = Array.isArray(d) ? d[0] : d;
      if (live) setOrgName(row?.name || "");
    });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const nodes = sections.map((item) => document.getElementById(item.id)).filter(Boolean);
    const observer = new IntersectionObserver((entries) => {
      const current = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (current) setActive(current.target.id);
    }, { rootMargin: "-15% 0px -70% 0px", threshold: [0, 0.2, 0.5] });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);
  const jump = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <SettingsLayout title="Sync API docs" description="Keep patients, appointments, leads and treatment plans aligned with your practice management system.">
      <div className="grid items-start gap-6 xl:grid-cols-[190px_minmax(0,1fr)_minmax(340px,38%)]">
        <aside className="hidden xl:sticky xl:top-6 xl:block">
          <p className="mb-3 text-caption font-semibold uppercase tracking-wide text-muted">Sync API</p>
          <label className="relative mb-5 flex h-10 items-center gap-2 rounded-sm border border-line bg-paper px-3">
            <Search size={14} className="text-muted" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search docs" className="min-w-0 flex-1 bg-transparent text-caption outline-none" />
          </label>
          <nav className="flex flex-col gap-4">
            {groups.map((group) => (
              <div key={group}>
                <p className="mb-1 px-2 text-caption font-semibold text-muted">{group}</p>
                {visible.filter((item) => item.group === group).map((item) => (
                  <a key={item.id} href={`#${item.id}`} aria-current={active === item.id ? "location" : undefined} className={`block border-l-2 px-2 py-2 text-caption ${active === item.id ? "border-ink font-semibold text-ink" : "border-transparent text-muted hover:text-ink"}`}>{item.label}</a>
                ))}
              </div>
            ))}
          </nav>
        </aside>
        <article className="min-w-0 overflow-hidden rounded-lg bg-paper shadow-soft">
          <div className="sticky top-0 z-20 border-b border-line bg-paper px-5 py-3 xl:hidden">
            <div className="flex items-center gap-3">
              <span className="shrink-0 text-caption font-semibold text-muted">Jump to</span>
              <SearchSelect value={active} onChange={jump} options={sections.map((item) => ({ value: item.id, label: item.label, hint: item.group }))} searchable searchPlaceholder="Search docs" ariaLabel="Jump to section" size="sm" className="min-w-0 flex-1" />
            </div>
          </div>
          <header className="border-b border-line px-6 py-7">
            <p className="text-caption font-semibold text-muted">{orgName === null ? <InlineLoader size={14} /> : (orgName || NO_DATA).toUpperCase()} · API REFERENCE</p>
            <h1 className="mt-2 text-h2 font-semibold">Practice management sync</h1>
            <p className="mt-3 max-w-2xl text-small leading-6 text-muted">Use the Sync API to keep clinic records consistent between Olli and your PMS. Requests use JSON over HTTPS and accept ISO 8601 timestamps.</p>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-caption">
              <span><span className="text-muted">Base URL</span>{" "}<code className="font-mono">{base}/sync</code></span>
              <span><span className="text-muted">Version</span>{" "}<code className="font-mono">v1</code></span>
            </div>
            <p className="mt-4 text-caption text-muted">Need messaging endpoints?{" "}<Link to="/waba/api-docs" className="font-semibold text-sky-deep underline">WhatsApp API docs</Link>.</p>
          </header>
          <div className="divide-y divide-line px-6">
            {sections.map((item) => (
              <section id={item.id} key={item.id} className="scroll-mt-8 py-7">
                <h2 className="text-h3 font-semibold">{item.label}</h2>
                {item.id === "overview" && (
                  <>
                    <p className="mt-3 max-w-2xl text-small leading-6 text-muted">Sync the patient record first, then reference it by your external ID from appointments and treatment plans. Every patient is deduplicated by phone number.</p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {[["Patients", "/sync/patients"], ["Appointments", "/sync/appointments"], ["Leads", "/sync/leads"], ["Treatment plans", "/sync/care-plans"]].map(([name, path]) => (
                        <div key={name} className="rounded-sm bg-mist p-3">
                          <p className="text-small font-semibold">{name}</p>
                          <code className="text-caption text-muted">{path}</code>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {item.id === "authentication" && (
                  <>
                    <p className="mt-3 text-small leading-6 text-muted">Send your API key in the X-API-Key header on every request. Keep live keys on your server and use test keys while validating a connector.</p>
                    <div className="mt-4 rounded-sm bg-mist p-4 font-mono text-small">X-API-Key: {KEY}</div>
                    <p className="mt-3 text-small"><Link to="/settings/integrations/api-keys" className="font-semibold text-sky-deep underline">Manage API keys</Link></p>
                  </>
                )}
                {["patients", "appointments", "leads", "treatment-plans"].includes(item.id) && (
                  <>
                    <p className="mt-3 max-w-2xl text-small leading-6 text-muted">Create or update records using your stable external ID. Include the location identifier when available.</p>
                    <div className="mt-3 rounded-sm bg-mist p-3 font-mono text-caption">{RESOURCES[item.id].routes}</div>
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full min-w-[460px] text-left text-caption">
                        <thead><tr className="border-b border-line text-muted"><th className="py-2 pr-3">Field</th><th className="py-2 pr-3">Type</th><th className="py-2">Notes</th></tr></thead>
                        <tbody>
                          {RESOURCES[item.id].fields.map((row) => (
                            <tr key={row[0]} className="border-b border-line last:border-0">
                              <td className="whitespace-nowrap py-2.5 pr-3 font-mono">{row[0]}</td>
                              <td className="whitespace-nowrap py-2.5 pr-3 text-muted">{row[1]}</td>
                              <td className="py-2.5 text-muted">{row[2]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {item.id === "bulk" && (
                  <p className="mt-3 text-small leading-6 text-muted">Send up to 100 records per bulk upsert. Each item uses the same external ID rules as a single-record request. Invalid items return per-record errors while valid records continue. The bulk routes are{" "}<code className="font-mono">POST /sync/patients/batch</code>, <code className="font-mono">/sync/practitioners/batch</code> and <code className="font-mono">/sync/appointments/batch</code>; include an{" "}<code className="font-mono">Idempotency-Key</code> header.</p>
                )}
                {item.id === "pagination" && (
                  <>
                    <p className="mt-3 text-small leading-6 text-muted">Use cursor pagination for large datasets. Pass{" "}<code className="font-mono">updated_since</code>{" "}to sync only changed records, and pass{" "}<code className="font-mono">next_cursor</code>{" "}back as <code className="font-mono">cursor</code> until it is null.</p>
                    <pre className="mt-4 overflow-x-auto rounded-sm bg-mist p-4 font-mono text-caption leading-6">GET /sync/patients?updated_since=2026-10-01T00:00:00Z&amp;limit=100</pre>
                  </>
                )}
                {item.id === "conflicts" && (
                  <p className="mt-3 text-small leading-6 text-muted">Records are matched by your external ID: sending the same ID again updates the record instead of creating a second one. A new patient is matched by phone number to a record Olli already has. A booking for a taken slot returns <code className="font-mono">409 SLOT_TAKEN</code> with available slots.</p>
                )}
                {item.id === "webhooks" && (
                  <>
                    <p className="mt-3 text-small leading-6 text-muted">Register a URL with <code className="font-mono">PUT /sync/webhooks</code> to receive record changes. Deliveries are signed with <code className="font-mono">X-GetOlli-Signature</code>. Acknowledge with any 2xx response within 10 seconds. Failed deliveries retry up to 7 times with backoff.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {EVENTS.map((event) => <code key={event} className="rounded-full bg-mist px-3 py-1.5 text-caption">{event}</code>)}
                    </div>
                  </>
                )}
                {item.id === "errors" && (
                  <>
                    <p className="mt-3 text-small text-muted">Errors include a stable code and a message. Retry rate limits after the suggested delay.</p>
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full min-w-[520px] text-left text-caption">
                        <tbody>
                          {ERRORS.map((row) => (
                            <tr key={row[1]} className="border-b border-line last:border-0">
                              <td className="whitespace-nowrap py-2 pr-3 font-mono">{row[0]}</td>
                              <td className="whitespace-nowrap py-2 pr-3 font-mono">{row[1]}</td>
                              <td className="py-2 text-muted">{row[2]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-4 text-small text-muted">Rate limit: 120 requests per minute per key. Bulk upsert: 100 records per request.</p>
                  </>
                )}
              </section>
            ))}
          </div>
        </article>
        <aside className="min-w-0 xl:sticky xl:top-6">
          <h2 className="mb-3 text-small font-semibold">Request examples</h2>
          <div className="flex flex-col gap-3">
            <ExampleCard title="Upsert patient" method="PUT" path="/sync/patients/MRN-88213" body={payload} />
            <ExampleCard title="Read changed patients" method="GET" path="/sync/patients?updated_since=2026-10-01T00%3A00%3A00Z" body={undefined} />
            <section className="rounded-lg bg-paper p-4 shadow-soft">
              <h3 className="text-caption font-semibold">Sync safety</h3>
              <ul className="mt-2 list-disc space-y-1 pl-4 text-caption leading-5 text-muted">
                <li>Use idempotency keys on every write.</li>
                <li>Keep external IDs stable across syncs.</li>
                <li>Use updated_since for incremental pulls.</li>
              </ul>
            </section>
          </div>
        </aside>
      </div>
    </SettingsLayout>
  );
}
